'use client';

const onboardingFeatures = [
  'Client completes a structured intake form',
  'AI analyses goals, experience, and limitations',
  'Baseline metrics are calculated automatically',
  'A professional client profile summary is generated',
  'Data is logged — ready to coach from day one',
];

const programmeFeatures = [
  'Structured training programme generated',
  'Exercise selection based on goals and constraints',
  'Sets, reps, and progression structure included',
  'Coaching notes attached for context',
  'You review, adjust, and deliver',
];

const echoFeatures = [
  'Personalised check-in reminders sent on schedule',
  'Each client gets their own check-in day',
  'Reminders link to a structured check-in form',
  'Clients submit energy, wins, injuries, sleep data',
  'All responses logged automatically',
];

const checkinFeatures = [
  'Automated weekly check-in reminders sent to clients',
  'Clients submit compliance, sleep, stress, mood, weight',
  'AI analyses trends and applies your coaching rules',
  'RAG status assigned: Green / Amber / Red',
  'Structured weekly review draft generated for your approval',
];

export default function FeaturesSection() {
  return (
    <section
      id="features"
      className="py-24 md:py-32 px-5 md:px-8 bg-white"
    >
      <div className="max-w-6xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <span className="badge badge-coral mb-4">The ElevateX AI Assistant System</span>
          <h2 className="display-md text-foreground mb-5">
            Four AI Assistants.
            <br />
            <span className="gradient-text">One Structured Backend.</span>
          </h2>
          <p className="text-lg text-muted max-w-2xl mx-auto">
            ElevateX installs a complete AI backend that works inside your coaching business.
          </p>
        </div>

        {/* Assistant 1: Onboarding */}
        <div className="bg-surface rounded-3xl p-8 md:p-10 border border-black/[0.07] mb-8">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/images/ac93e3c3-a534-4cdc-9b73-e22466c38219_removalai_preview-1774538078746.png"
                    alt="AXIS — AI Client Onboarding Assistant"
                    className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 shadow-md"
                  />
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">AXIS</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">AI Client Onboarding Assistant</h3>
              <p className="text-muted leading-relaxed mb-4">
                When a new client signs up, the AI handles everything — from intake to structured summary.
              </p>
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2">
                <span className="text-xs font-bold text-primary uppercase tracking-wider">You receive a clean, structured onboarding summary — ready to coach.</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-muted uppercase tracking-wider mb-4">What happens automatically:</p>
              <ul className="space-y-3">
                {onboardingFeatures?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted mt-5 italic">No manual form reading. No messy notes.</p>
            </div>
          </div>
        </div>

        {/* Assistant 2: Programme Builder */}
        <div className="bg-surface rounded-3xl p-8 md:p-10 border border-black/[0.07] mb-8">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="flex items-center gap-4 mb-5">
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center shrink-0">
                  <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                </div>
                <div className="flex items-center gap-3">
                  <img
                    src="/assets/images/0c0d750d-6c69-4415-a6ed-5e277486b703_removalai_preview-1774538310955.png"
                    alt="FORGE — AI Programme Builder Assistant"
                    className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 shadow-md"
                  />
                  <span className="text-xs font-bold text-primary uppercase tracking-wider">FORGE</span>
                </div>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">AI Programme Builder Assistant</h3>
              <p className="text-muted leading-relaxed mb-4">
                Using client goals, experience level, constraints, and your coaching philosophy — the AI generates a complete training programme.
              </p>
              <div className="inline-flex items-center gap-2 bg-primary/10 rounded-full px-4 py-2">
                <span className="text-xs font-bold text-primary uppercase tracking-wider">No more starting from a blank page.</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-muted uppercase tracking-wider mb-4">What the AI generates:</p>
              <ul className="space-y-3">
                {programmeFeatures?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Assistant 3: ECHO — Check-In Reminder */}
        <div className="bg-surface rounded-3xl p-8 md:p-10 border border-black/[0.07] mb-8">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5" style={{ backgroundColor: 'rgba(255, 99, 71, 0.15)' }}>
                <svg className="w-7 h-7" style={{ color: '#FF6347' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <div className="flex items-center gap-3">
                <img
                  src="/assets/images/f58a295b-8a4a-4153-a3f0-619deaf3e925_removalai_preview-1774538420233.png"
                  alt="ECHO — AI Check-In Reminder Assistant"
                  className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 shadow-md"
                />
                <span className="text-xs font-bold text-primary uppercase tracking-wider">ECHO</span>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">AI Check-In Reminder Assistant</h3>
              <p className="text-muted leading-relaxed mb-4">
                Clients receive automated check-in reminders on the days you set — no manual chasing, no missed weeks.
              </p>
              <div className="inline-flex items-center gap-2 rounded-full px-4 py-2" style={{ backgroundColor: 'rgba(255, 99, 71, 0.1)' }}>
                <span className="text-xs font-bold uppercase tracking-wider" style={{ color: '#FF6347' }}>YOU NEVER CHASE A CLIENT AGAIN.</span>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-muted uppercase tracking-wider mb-4">What happens automatically:</p>
              <ul className="space-y-3">
                {echoFeatures?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#FF6347' }} fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <p className="text-sm text-muted mt-5 italic">No spreadsheets. No WhatsApp messages. No forgotten clients.</p>
            </div>
          </div>
        </div>

        {/* Assistant 4: Weekly Check-In / NOVA */}
        <div className="bg-surface rounded-3xl p-8 md:p-10 border border-black/[0.07]">
          <div className="grid lg:grid-cols-[1fr_1.4fr] gap-10 items-start">
            <div>
              <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-5">
                <svg className="w-7 h-7 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              </div>
              <div className="flex items-center gap-3 mb-5">
                <img
                  src="/assets/images/0109dd61-25ad-45b1-ba19-e49a09e5ee5b_removalai_preview-1774538545710.png"
                  alt="NOVA — AI Weekly Review Assistant"
                  className="w-14 h-14 rounded-full object-cover border-2 border-primary/20 shadow-md"
                />
                <span className="text-xs font-bold text-primary uppercase tracking-wider">NOVA</span>
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-3">AI Weekly Review Assistant</h3>
              <p className="text-muted leading-relaxed mb-5">
                Each week, the AI analyses submitted check-in data and generates a structured review draft — ready for your approval.
              </p>
              {/* RAG Status */}
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted uppercase tracking-wider mb-3">RAG Status Assigned:</p>
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">🟢</span>
                  <span className="text-sm font-semibold text-foreground">Green — On Track</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">🟠</span>
                  <span className="text-sm font-semibold text-foreground">Amber — Needs Attention</span>
                </div>
                <div className="flex items-center gap-2.5">
                  <span className="text-lg">🔴</span>
                  <span className="text-sm font-semibold text-foreground">Red — High Risk</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold text-muted uppercase tracking-wider mb-4">Weekly review draft includes:</p>
              <ul className="space-y-3 mb-6">
                {checkinFeatures?.map((feature, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <svg className="w-5 h-5 text-primary shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
              <div className="bg-white rounded-2xl p-5 border border-black/[0.07]">
                <p className="text-xs font-bold text-muted uppercase tracking-wider mb-3">Each review draft includes:</p>
                <div className="grid grid-cols-2 gap-2">
                  {['Status', 'Summary', 'Risk Flags', 'Adjustments', 'Coach Message', 'Trend Analysis']?.map((item) => (
                    <div key={item} className="flex items-center gap-2">
                      <div className="w-1.5 h-1.5 rounded-full bg-primary shrink-0" />
                      <span className="text-xs text-foreground font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}